import React, {useMemo} from 'react';
import type {AChartXProps} from '@atiautomacao/ati-ui-library';
import ManagementOverviewAChartX from './ManagementOverviewAChartX';
import ChartLegendPanel from './ChartLegendPanel';
import ReportSectionTitle from './ReportSectionTitle';
import {OverviewChartData, ReferencePeriod} from '../types/ManagementOverviewReport.types';
import {
    buildBasicPrChartOption,
    buildMonthlyIrradianceChartOption,
    buildNormalizedPrChartOption,
    buildWeeklyGenerationChartOption,
    MANAGEMENT_OVERVIEW_BASIC_PR_LEGEND,
    MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX,
    MANAGEMENT_OVERVIEW_GENERATION_LEGEND,
    MANAGEMENT_OVERVIEW_IRRADIANCE_LEGEND,
    MANAGEMENT_OVERVIEW_NORMALIZED_PR_LEGEND,
} from '../utils/managementOverviewChartOptions';
import {buildManagementOverviewChartId} from '../utils/managementOverviewChartIds';

interface ReportChartsProps {
    stationId: number;
    charts: OverviewChartData;
    page: 'one' | 'two';
    referencePeriod?: ReferencePeriod;
    onChartReady?: (chartId: string) => void;
}

const ReportCharts: React.FC<ReportChartsProps> = ({
    stationId,
    charts,
    page,
    referencePeriod,
    onChartReady,
}) => {
    const asChartOption = (option: object): AChartXProps['option'] => option as AChartXProps['option'];
    const periodAside =
        referencePeriod != null
            ? `${referencePeriod.month} ${referencePeriod.year}`
            : undefined;
    const yearAside =
        referencePeriod != null ? `Ano de ${referencePeriod.year}` : undefined;

    const generationOption = useMemo(
        () => asChartOption(buildWeeklyGenerationChartOption(charts.weekly)),
        [charts.weekly]
    );
    const irradianceOption = useMemo(
        () => asChartOption(buildMonthlyIrradianceChartOption(charts.monthly)),
        [charts.monthly]
    );
    const basicPrOption = useMemo(
        () => asChartOption(buildBasicPrChartOption(charts.monthly)),
        [charts.monthly]
    );
    const normalizedPrOption = useMemo(
        () => asChartOption(buildNormalizedPrChartOption(charts.monthly)),
        [charts.monthly]
    );

    if (page === 'one') {
        return (
            <>
                <section className="management-overview-chart-block avoid-break">
                    <ReportSectionTitle>Geração de Energia</ReportSectionTitle>
                    <div className="management-overview-chart-row">
                        <div
                            className="management-overview-chart"
                            style={{ height: MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX }}
                        >
                            <ManagementOverviewAChartX
                                chartId={buildManagementOverviewChartId(stationId, 'generation')}
                                option={generationOption}
                                onChartReady={onChartReady}
                            />
                        </div>
                        <ChartLegendPanel items={MANAGEMENT_OVERVIEW_GENERATION_LEGEND} aside={periodAside} />
                    </div>
                </section>

                <section className="management-overview-chart-block avoid-break">
                    <ReportSectionTitle>Irradiação</ReportSectionTitle>
                    <div className="management-overview-chart-row">
                        <div
                            className="management-overview-chart"
                            style={{ height: MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX }}
                        >
                            <ManagementOverviewAChartX
                                chartId={buildManagementOverviewChartId(stationId, 'irradiance')}
                                option={irradianceOption}
                                onChartReady={onChartReady}
                            />
                        </div>
                        <ChartLegendPanel items={MANAGEMENT_OVERVIEW_IRRADIANCE_LEGEND} aside={yearAside} />
                    </div>
                </section>
            </>
        );
    }

    return (
        <>
            <section className="management-overview-chart-block avoid-break">
                <div className="management-overview-chart-row">
                    <div className="management-overview-chart-block__chart-area">
                        <ReportSectionTitle aside={yearAside}>PR Básico Mensal</ReportSectionTitle>
                        <div
                            className="management-overview-chart"
                            style={{ height: MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX }}
                        >
                            <ManagementOverviewAChartX
                                chartId={buildManagementOverviewChartId(stationId, 'pr-basic')}
                                option={basicPrOption}
                                onChartReady={onChartReady}
                            />
                        </div>
                    </div>
                    <ChartLegendPanel items={MANAGEMENT_OVERVIEW_BASIC_PR_LEGEND} aside={yearAside} />
                </div>
            </section>

            <section className="management-overview-chart-block avoid-break">
                <div className="management-overview-chart-row">
                    <div className="management-overview-chart-block__chart-area">
                        <ReportSectionTitle aside={yearAside}>PR Normalizado Mensal</ReportSectionTitle>
                        <div
                            className="management-overview-chart"
                            style={{ height: MANAGEMENT_OVERVIEW_CHART_HEIGHT_PX }}
                        >
                            <ManagementOverviewAChartX
                                chartId={buildManagementOverviewChartId(stationId, 'pr-normalized')}
                                option={normalizedPrOption}
                                onChartReady={onChartReady}
                            />
                        </div>
                    </div>
                    <ChartLegendPanel items={MANAGEMENT_OVERVIEW_NORMALIZED_PR_LEGEND} aside={yearAside} />
                </div>
            </section>
        </>
    );
};

export default ReportCharts;
